import Axios from "axios";
import { CardElementsProvider } from "context/stripe-provider";
import useAnalytics from "hooks/analytics";
import useAuthUser from "hooks/auth-user";
import useFeatureManager from "hooks/featureManager";
import useLanguage from "hooks/language";
import { FollowUpType } from "models/appointment";
import { useCallback, useEffect, useState } from "react";
import { useAsyncCallback } from "react-async-hook";
import { trackBookingFlowPayment } from "utils/analytics/booking-flow-tracking";
import {
    BookingFlowPaymentDueSummary,
    getBookingFlowPayButtonLabel,
    isBookingFlowZeroDueNowPayment,
} from "utils/booking-flow-payment-due-summary";
import BookingFlowScreeningSwitchModal from "./booking-flow-screening-switch-modal";
import ENDPOINTS from "utils/endpoints";
import PaymentFieldComponent from "../../pages/home/screening/payment-field/payment-field";
import PaymentMethodsBusy from "../../pages/home/screening/payment-field/payment-methods-busy";
import useCardPlaceholder from "../../pages/home/screening/payment-field/use-card-placeholder";
import useCardToken from "../../pages/home/screening/payment-field/use-card-token";
import { ScreeningPaymentType } from "../../pages/home/screening/payment-types";
import {
    EXPRESS_WALLET_CHECKOUT_FLAG,
    WalletCheckoutFlowValue,
} from "../../pages/home/screening/payment-field/express-wallet-checkout";
import {
    BookingFlowExpressWalletSection,
    BookingFlowPaymentMethodStack,
    PaymentMethodsLabel,
    isSecureExpressContext,
} from "./booking-flow-payment-methods";
import { FeatureFlag } from "utils/feature-management/amplitude";
import BookingFlowPaymentFooterDisclaimers from "./booking-flow-payment-footer-disclaimers";
import BookingFlowPrimaryButton from "./booking-flow-primary-button";
import styles from "./booking-flow-payment.module.css";

function PaymentDueStrip({
    dueNowLabel,
    dueAtClinicLabel,
}: {
    dueNowLabel: string;
    dueAtClinicLabel: string;
}): JSX.Element {
    const { translations } = useLanguage();
    const t = translations.bookingFlow.payment;

    return (
        <div className={styles.dueStrip}>
            <div className={styles.dueCell}>
                <div className={styles.dueLabel}>{t.dueNow}</div>
                <div className={styles.dueAmount}>{dueNowLabel}</div>
            </div>
            <div className={styles.dueCell}>
                <div className={styles.dueLabel}>{t.dueAtClinic}</div>
                <div className={styles.dueAmount}>{dueAtClinicLabel}</div>
            </div>
        </div>
    );
}

function PaymentAcknowledgment({
    clinicAmountLabel,
    checked,
    onToggle,
    htmlOverride,
}: {
    clinicAmountLabel: string;
    checked: boolean;
    onToggle: () => void;
    htmlOverride?: string;
}): JSX.Element {
    const { translations } = useLanguage();
    const html =
        htmlOverride ??
        translations.bookingFlow.payment.acknowledgment.replace(
            "{clinicAmount}",
            clinicAmountLabel
        );

    return (
        <div
            role="button"
            tabIndex={0}
            className={styles.ack}
            onClick={onToggle}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onToggle();
                }
            }}
        >
            <span
                className={`${styles.ackCheckbox} ${
                    checked ? styles.ackCheckboxChecked : ""
                }`}
                aria-hidden="true"
            >
                {checked ? "✓" : ""}
            </span>
            <span
                className={styles.ackText}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
}

export type BookingFlowScreeningPaymentProps = {
    dueSummary: BookingFlowPaymentDueSummary;
    walletCheckoutFlow: WalletCheckoutFlowValue;
    paymentFollowUpType: FollowUpType;
    onSelectFollowUp: (followUpType: FollowUpType) => void;
    onCompletePayment: (
        hasValidCreditCard: boolean,
        resolvedFollowUpType: FollowUpType
    ) => Promise<void> | void;
    footerCancellationOverride?: string;
    showDelayWarning?: boolean;
};

export default function BookingFlowScreeningPayment({
    dueSummary,
    walletCheckoutFlow,
    paymentFollowUpType,
    onSelectFollowUp,
    onCompletePayment,
    footerCancellationOverride,
    showDelayWarning = true,
}: BookingFlowScreeningPaymentProps): JSX.Element {
    if (dueSummary.showPaymentMethods) {
        return (
            <BookingFlowScreeningOnlinePayment
                dueSummary={dueSummary}
                walletCheckoutFlow={walletCheckoutFlow}
                paymentFollowUpType={paymentFollowUpType}
                onSelectFollowUp={onSelectFollowUp}
                onCompletePayment={onCompletePayment}
                footerCancellationOverride={footerCancellationOverride}
                showDelayWarning={showDelayWarning}
            />
        );
    }

    return (
        <BookingFlowScreeningClinicOnlyPayment
            dueSummary={dueSummary}
            walletCheckoutFlow={walletCheckoutFlow}
            paymentFollowUpType={paymentFollowUpType}
            onSelectFollowUp={onSelectFollowUp}
            onCompletePayment={onCompletePayment}
            footerCancellationOverride={footerCancellationOverride}
            showDelayWarning={showDelayWarning}
        />
    );
}

type InnerProps = BookingFlowScreeningPaymentProps;

function BookingFlowScreeningClinicOnlyPayment({
    dueSummary,
    paymentFollowUpType,
    onSelectFollowUp,
    onCompletePayment,
    footerCancellationOverride,
    showDelayWarning = true,
}: InnerProps): JSX.Element {
    const [authUser] = useAuthUser();
    const { translations, language } = useLanguage();
    const [analytics] = useAnalytics();
    const tForm = translations.upfrontPaymentSection.paymentForm;
    const [clinicAcknowledged, setClinicAcknowledged] = useState(false);

    const needsAck = dueSummary.showClinicAcknowledgment && !clinicAcknowledged;

    const handleSubmit = useAsyncCallback(async () => {
        if (!authUser?.lastUserQuestionnaire || needsAck) {
            return;
        }
        onSelectFollowUp(paymentFollowUpType);
        trackBookingFlowPayment(analytics, ScreeningPaymentType.Cash);
        await onCompletePayment(false, paymentFollowUpType);
    });

    const buttonLabel = getBookingFlowPayButtonLabel(
        language,
        dueSummary.dueNowDollars,
        tForm.confirmButtonText,
        tForm.payButtonPrefix
    );

    return (
        <>
            <PaymentDueStrip
                dueNowLabel={dueSummary.dueNowLabel}
                dueAtClinicLabel={dueSummary.dueAtClinicLabel}
            />
            {dueSummary.showClinicAcknowledgment && (
                <PaymentAcknowledgment
                    clinicAmountLabel={dueSummary.dueAtClinicLabel}
                    checked={clinicAcknowledged}
                    onToggle={() =>
                        setClinicAcknowledged((checked) => !checked)
                    }
                />
            )}
            <BookingFlowPrimaryButton
                loading={handleSubmit.loading}
                disabled={needsAck}
                onClick={() => handleSubmit.execute()}
            >
                {buttonLabel}
            </BookingFlowPrimaryButton>
            <BookingFlowPaymentFooterDisclaimers
                cancellationText={resolveFooterCancellationText(
                    footerCancellationOverride
                )}
                showDelayWarning={showDelayWarning}
            />
        </>
    );
}

type OnlineCardCheckoutProps = Omit<
    InnerProps,
    "footerCancellationOverride" | "showDelayWarning"
> & {
    needsAck: boolean;
    isWalletProcessing: boolean;
    clinicAcknowledged: boolean;
    onClinicAckToggle: () => void;
    cardInfoLoading: boolean;
    existingCardInfo: ReturnType<typeof useCardPlaceholder>["result"];
};

function BookingFlowScreeningOnlineCardCheckout({
    dueSummary,
    paymentFollowUpType,
    onSelectFollowUp,
    onCompletePayment,
    needsAck,
    isWalletProcessing,
    clinicAcknowledged,
    onClinicAckToggle,
    cardInfoLoading,
    existingCardInfo,
}: OnlineCardCheckoutProps): JSX.Element {
    const [authUser] = useAuthUser();
    const { translations, language } = useLanguage();
    const [analytics] = useAnalytics();
    const tForm = translations.upfrontPaymentSection.paymentForm;
    const getCardTokenCallback = useCardToken();

    const isUserCardAvailable = !cardInfoLoading && !!existingCardInfo;
    const [isCardFieldValid, setIsCardFieldValid] = useState(
        isUserCardAvailable
    );

    useEffect(() => {
        if (isUserCardAvailable) {
            setIsCardFieldValid(true);
        }
    }, [isUserCardAvailable]);

    const handleSubmit = useAsyncCallback(async () => {
        if (!authUser?.lastUserQuestionnaire || needsAck) {
            return;
        }

        onSelectFollowUp(paymentFollowUpType);

        const cardToken = await getCardTokenCallback();
        if (cardToken) {
            await Axios.put(ENDPOINTS.USER_PAYMENT_INFO, {
                token: cardToken,
            });
        }
        if (cardToken || isUserCardAvailable) {
            const method = cardToken
                ? ScreeningPaymentType.CreditCard
                : ScreeningPaymentType.CardOnFile;
            trackBookingFlowPayment(analytics, method);
            await onCompletePayment(true, paymentFollowUpType);
        } else {
            setIsCardFieldValid(false);
        }
    });

    const buttonLabel = getBookingFlowPayButtonLabel(
        language,
        dueSummary.dueNowDollars,
        tForm.confirmButtonText,
        tForm.payButtonPrefix
    );

    const isSubmitDisabled =
        needsAck || isWalletProcessing || !isCardFieldValid;

    return (
        <>
            <PaymentFieldComponent
                layout="bookingFlow"
                loading={cardInfoLoading}
                setIsValid={setIsCardFieldValid}
                existingCardInfo={cardInfoLoading ? null : existingCardInfo}
            />
            {dueSummary.showClinicAcknowledgment && (
                <PaymentAcknowledgment
                    clinicAmountLabel={dueSummary.dueAtClinicLabel}
                    checked={clinicAcknowledged}
                    onToggle={onClinicAckToggle}
                    htmlOverride={
                        isPublicPhoneCallPayment(
                            paymentFollowUpType,
                            dueSummary
                        )
                            ? translations.bookingFlow.payment
                                  .cardOnFileAcknowledgment
                            : undefined
                    }
                />
            )}
            <BookingFlowPrimaryButton
                loading={handleSubmit.loading}
                disabled={isSubmitDisabled}
                onClick={() => handleSubmit.execute()}
            >
                {buttonLabel}
            </BookingFlowPrimaryButton>
        </>
    );
}

function isPublicPhoneCallPayment(
    paymentFollowUpType: FollowUpType,
    dueSummary: BookingFlowPaymentDueSummary
): boolean {
    return (
        paymentFollowUpType === FollowUpType.Regular &&
        dueSummary.dueNowDollars <= 0 &&
        dueSummary.showPaymentMethods
    );
}

function resolveFooterCancellationText(
    footerCancellationOverride: string | undefined
): string | undefined {
    return footerCancellationOverride;
}

function BookingFlowScreeningOnlinePayment({
    dueSummary,
    walletCheckoutFlow,
    paymentFollowUpType,
    onSelectFollowUp,
    onCompletePayment,
    footerCancellationOverride,
    showDelayWarning = true,
}: InnerProps): JSX.Element {
    const { translations } = useLanguage();
    const footerCancellationText = resolveFooterCancellationText(
        footerCancellationOverride
    );
    const [analytics] = useAnalytics();
    const tUpfront = translations.upfrontPaymentSection;
    const tForm = tUpfront.paymentForm;
    const tPayment = translations.bookingFlow.payment;
    const [featureManager] = useFeatureManager();
    const cardInfo = useCardPlaceholder();
    const [isWalletProcessing, setIsWalletProcessing] = useState(false);
    const [clinicAcknowledged, setClinicAcknowledged] = useState(false);
    const [isExpressInitialized, setIsExpressInitialized] = useState(false);

    const handleExpressInitialized = useCallback(() => {
        setIsExpressInitialized(true);
    }, []);

    const needsAck = dueSummary.showClinicAcknowledgment && !clinicAcknowledged;

    const saveCardOnFileWallet = dueSummary.dueNowDollars <= 0;
    const mightUseWalletCheckout =
        dueSummary.showPaymentMethods && isSecureExpressContext();
    const isWalletCheckoutEnabled =
        mightUseWalletCheckout &&
        !!featureManager &&
        featureManager.isFeatureFlagEnabled(
            EXPRESS_WALLET_CHECKOUT_FLAG,
            FeatureFlag.On
        );
    const isWaitingForFeatureManager =
        mightUseWalletCheckout && !featureManager;
    const isWaitingForExpressInit =
        isWalletCheckoutEnabled && !isExpressInitialized;
    const isPaymentMethodsLoading =
        cardInfo.loading ||
        isWaitingForFeatureManager ||
        isWaitingForExpressInit;

    const isZeroDueNowPayment = isBookingFlowZeroDueNowPayment(dueSummary);
    const paymentMethodsSectionTitle = isZeroDueNowPayment
        ? tPayment.guaranteeCardSection
        : undefined;

    const [
        isCashAcknowledgmentModalOpen,
        setIsCashAcknowledgmentModalOpen,
    ] = useState(false);

    const handleCashAcknowledge = useAsyncCallback(async () => {
        onSelectFollowUp(paymentFollowUpType);
        trackBookingFlowPayment(analytics, ScreeningPaymentType.Cash);
        await onCompletePayment(false, paymentFollowUpType);
        setIsCashAcknowledgmentModalOpen(false);
    });

    const handleWalletPaid = async (method: string) => {
        if (needsAck) {
            return;
        }
        onSelectFollowUp(paymentFollowUpType);
        trackBookingFlowPayment(analytics, method);
        await onCompletePayment(true, paymentFollowUpType);
    };

    return (
        <>
            <PaymentDueStrip
                dueNowLabel={dueSummary.dueNowLabel}
                dueAtClinicLabel={dueSummary.dueAtClinicLabel}
            />
            <PaymentMethodsBusy
                busy={isPaymentMethodsLoading}
                hideContentWhileBusy
                label={tPayment.loadingPaymentMethods}
                wrapClassName={styles.paymentMethodsLoadingWrap}
            >
                <PaymentMethodsBusy
                    busy={isWalletProcessing}
                    label={tForm.walletProcessing}
                >
                    <BookingFlowPaymentMethodStack>
                        {isWalletCheckoutEnabled ? (
                            <BookingFlowExpressWalletSection
                                flow={walletCheckoutFlow}
                                saveCardOnFile={saveCardOnFileWallet}
                                disabled={needsAck}
                                onInitialized={handleExpressInitialized}
                                onPaid={handleWalletPaid}
                                onProcessingChange={setIsWalletProcessing}
                                walletPaintSignalEnabled={!cardInfo.loading}
                                paymentMethodsSectionTitle={
                                    paymentMethodsSectionTitle
                                }
                            />
                        ) : (
                            <PaymentMethodsLabel
                                sectionTitle={paymentMethodsSectionTitle}
                            />
                        )}
                        <CardElementsProvider>
                            <BookingFlowScreeningOnlineCardCheckout
                                dueSummary={dueSummary}
                                walletCheckoutFlow={walletCheckoutFlow}
                                paymentFollowUpType={paymentFollowUpType}
                                onSelectFollowUp={onSelectFollowUp}
                                onCompletePayment={onCompletePayment}
                                needsAck={needsAck}
                                isWalletProcessing={isWalletProcessing}
                                clinicAcknowledged={clinicAcknowledged}
                                onClinicAckToggle={() =>
                                    setClinicAcknowledged((checked) => !checked)
                                }
                                cardInfoLoading={cardInfo.loading}
                                existingCardInfo={cardInfo.result}
                            />
                        </CardElementsProvider>
                    </BookingFlowPaymentMethodStack>
                </PaymentMethodsBusy>
            </PaymentMethodsBusy>
            {!isPaymentMethodsLoading && (
                <BookingFlowPaymentFooterDisclaimers
                    cancellationText={footerCancellationText}
                    showDelayWarning={showDelayWarning}
                    delayText={
                        isPublicPhoneCallPayment(
                            paymentFollowUpType,
                            dueSummary
                        )
                            ? tPayment.noChargeTodayFooter
                            : undefined
                    }
                />
            )}
            {isZeroDueNowPayment && !isPaymentMethodsLoading && (
                <p className={styles.cashPaymentLink}>
                    <button
                        type="button"
                        className={styles.cashPaymentLinkButton}
                        disabled={
                            isWalletProcessing || handleCashAcknowledge.loading
                        }
                        onClick={() => setIsCashAcknowledgmentModalOpen(true)}
                    >
                        {tUpfront.public.cashPaymentInClinic}
                    </button>
                </p>
            )}
            {isCashAcknowledgmentModalOpen && (
                <BookingFlowScreeningSwitchModal
                    title={tPayment.cashAcknowledgmentModalTitle}
                    bodyHtml={tPayment.cashAcknowledgmentModalBody}
                    confirmLabel={tPayment.cashAcknowledgmentConfirm}
                    cancelLabel={tPayment.cashAcknowledgmentRefuse}
                    confirmLoading={handleCashAcknowledge.loading}
                    onConfirm={() => handleCashAcknowledge.execute()}
                    onCancel={() => setIsCashAcknowledgmentModalOpen(false)}
                />
            )}
        </>
    );
}
